import { IM_WS_URL } from '../constants/endpoints';
import { getMemberToken } from '../auth/tokenStorage';

/**
 * Native WebSocket singleton for Formosa IM.
 * Do NOT log full URL or token. Do NOT use Socket.IO.
 */
class ImWebSocketClient {
  constructor() {
    this.socket = null;
    this.status = 'idle';
    this.listeners = new Set();
    this.statusListeners = new Set();
    this.reconnectAttempt = 0;
    this.reconnectTimer = null;
    this.pingTimer = null;
    this.pongTimer = null;
    this.manualClose = false;
    this.enabled = false;
    this.lastToken = null;

    this._onOnline = () => {
      if (this.enabled && this.status !== 'open') {
        this.connect();
      }
    };
    this._onVisibility = () => {
      if (document.visibilityState === 'visible' && this.enabled && this.status !== 'open') {
        this.connect();
      }
    };
  }

  getStatus() {
    return this.status;
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener) {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  enable() {
    this.enabled = true;
    this.manualClose = false;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this._onOnline);
      document.addEventListener('visibilitychange', this._onVisibility);
    }
    this.connect();
  }

  disable() {
    this.enabled = false;
    this.manualClose = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this._onOnline);
      document.removeEventListener('visibilitychange', this._onVisibility);
    }
    this._clearTimers();
    this._closeSocket(1000, 'logout');
    this._setStatus('idle');
  }

  /** Call after token refresh / login. */
  reconnectWithToken() {
    this.manualClose = false;
    this.enabled = true;
    this.reconnectAttempt = 0;
    this._closeSocket(1000, 'token refresh');
    this.connect();
  }

  connect() {
    if (!this.enabled) {
      return;
    }
    // IM 服務停用（IM_WS_URL 為空）：不連線、不排程重連
    if (!IM_WS_URL) {
      this._setStatus('idle');
      return;
    }
    const token = getMemberToken();
    if (!token) {
      this._setStatus('disconnected');
      return;
    }
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING) &&
      this.lastToken === token
    ) {
      return;
    }
    this._closeSocket();
    this.lastToken = token;
    this._setStatus('connecting');
    let socket;
    try {
      // Query token required for browsers; never log this URL.
      const url = `${IM_WS_URL}?token=${encodeURIComponent(token)}`;
      socket = new WebSocket(url);
    } catch {
      this._scheduleReconnect();
      return;
    }
    this.socket = socket;
    socket.onopen = () => {
      this.reconnectAttempt = 0;
      this._setStatus('open');
      this._startHeartbeat();
    };
    socket.onmessage = (event) => {
      this._onPongActivity();
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch {
        return;
      }
      if (!payload || !payload.type) {
        return;
      }
      if (payload.type === 'PONG') {
        return;
      }
      this.listeners.forEach((fn) => {
        try {
          fn(payload);
        } catch {
          /* ignore consumer errors */
        }
      });
    };
    socket.onerror = () => {
      // Status handled in onclose
    };
    socket.onclose = () => {
      this._clearHeartbeat();
      this.socket = null;
      if (this.manualClose || !this.enabled) {
        this._setStatus('disconnected');
        return;
      }
      this._setStatus('disconnected');
      this._scheduleReconnect();
    };
  }

  sendPing() {
    this._send({ type: 'PING' });
  }

  _send(obj) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    try {
      this.socket.send(JSON.stringify(obj));
    } catch {
      /* ignore */
    }
  }

  _setStatus(next) {
    if (this.status === next) {
      return;
    }
    this.status = next;
    this.statusListeners.forEach((fn) => {
      try {
        fn(next);
      } catch {
        /* ignore */
      }
    });
  }

  _startHeartbeat() {
    this._clearHeartbeat();
    this.pingTimer = setInterval(() => {
      this.sendPing();
      this._armPongTimeout();
    }, 25000);
  }

  _armPongTimeout() {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
    }
    this.pongTimer = setTimeout(() => {
      this._closeSocket(1001, 'pong timeout');
    }, 15000);
  }

  _onPongActivity() {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  _clearHeartbeat() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  _clearTimers() {
    this._clearHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  _scheduleReconnect() {
    if (!this.enabled || this.manualClose) {
      return;
    }
    if (this.reconnectTimer) {
      return;
    }
    const attempt = this.reconnectAttempt++;
    const base = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));
    const jitter = Math.floor(Math.random() * 400);
    this._setStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, base + jitter);
  }

  _closeSocket(code, reason) {
    this._clearHeartbeat();
    if (!this.socket) {
      return;
    }
    try {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket.onclose = null;
      if (
        this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING
      ) {
        this.socket.close(code || 1000, reason || '');
      }
    } catch {
      /* ignore */
    }
    this.socket = null;
  }
}

const imWebSocketClient = new ImWebSocketClient();
export default imWebSocketClient;
